import {
  ReportableEntity,
  ReportColumnsClaim,
  ReportColumnsRegistration,
  ReportColumnsShipping,
} from 'corso-types';
import { formatFilterValue } from 'corso-admin-ui';
import type { Description } from '~/components/DescriptionList';
import {
  Filter,
  FilterDefinition,
} from '~/components/searchTable/searchControlBar/useFilterDefinition';

export const isClaimEntity = (
  entity: ReportableEntity,
): entity is 'Return' | 'Warranty' =>
  entity === 'Return' || entity === 'Warranty';

export const columnsByEntity = {
  Return: ReportColumnsClaim,
  Warranty: ReportColumnsClaim,
  Registration: ReportColumnsRegistration,
  Shipping: ReportColumnsShipping,
} as const satisfies Record<ReportableEntity, unknown>;

export const pluralEntityName = {
  Return: 'Return Claims',
  Warranty: 'Warranty Claims',
  Registration: 'Registrations',
  Shipping: 'Shipping Claims',
} as const satisfies Record<ReportableEntity, string>;

// Walks a filter record + its definition, producing one DescriptionList row per
// filter present. The `as Filter` cast crosses an Object.entries dynamic-key
// boundary — same pattern other consumers of `useFilterDefinition` use.
export const filtersToDescriptions = (
  filters: Record<string, unknown>,
  filterDefinition: FilterDefinition,
  options?: { includeSearchTerm?: boolean },
): Description[] => {
  const out: Description[] = [];
  Object.entries(filters).forEach(([id, value]) => {
    if (id === 'searchTerm' && !options?.includeSearchTerm) return;
    if (!(id in filterDefinition.filters)) return;
    const key = id as keyof typeof filterDefinition.filters; // checked via `in` above
    const def = filterDefinition.filters[key];
    const filter = { ...def, value } as Filter;
    out.push({ term: filter.label, details: formatFilterValue(filter) });
  });
  return out;
};

export const AllReportColumns = {
  ...ReportColumnsRegistration,
  ...ReportColumnsShipping,
  ...ReportColumnsClaim,
} as const;

export const exportColumnDisplayName = {
  // shared
  customer_name: 'Customer Name',
  customer_email: 'Customer Email',
  created_date: 'Created Date',
  // claim
  claim_id: 'Claim ID',
  tags: 'Tags',
  line_item_media: 'Claim Media',
  original_order_name: 'Original Order Name',
  original_order_date: 'Original Order Date',
  original_order_id: 'Original Order ID',
  status: 'Status',
  product_name: 'Product Name',
  product_sku: 'Product SKU',
  price: 'Price',
  quantity: 'Quantity',
  return_tracking_number: 'Return Tracking Number',
  line_item_status: 'Line Item Status',
  line_item_status_detail: 'Line Item Status Detail',
  line_item_reason: 'Line Item Reason',
  line_item_reason_detail: 'Line Item Reason Detail',
  line_item_note_to_customer: 'Line Item Note To Customer',
  line_item_note_from_customer: 'Line Item Note From Customer',
  requested_resolution_method: 'Requested Resolution Method',
  resolution_method: 'Resolution Method',
  finalized_date: 'Finalized Date',
  inspection_grade: 'Inspection Grade',
  inspection_media: 'Inspection Media',
  inspection_date: 'Inspection Date',
  fee_applied: 'Fee Applied',
  return_shipping_fee_applied: 'Return Shipping Fee Applied',
  exchange_order_shipping_fee_applied: 'Exchange Order Shipping Fee Applied',
  fee_name: 'Fee Name',

  // registration
  registration_id: 'Registration ID',
  channel: 'Channel',
  estimated_purchase_date: 'Estimated Purchase Date',
  proof_of_purchase_file_urls: 'Proof of Purchase File URLs',
  canceled_date: 'Canceled Date',

  customer_created_on_platform: 'Customer Created On Platform',
  email_marketing_consent_collected: 'Email Marketing Consent Collected',
  email_marketing_consent_updated_on_platform:
    'Email Marketing Consent Updated On Platform',
  incentive_discount_code: 'Incentive Discount Code',
  incentive_discount_expires_on: 'Incentive Discount Expires On',

  // shipping
  shipping_claim_id: 'Shipping Claim ID',
  reason: 'Reason',
  original_order_tracking_number: 'Original Order Tracking Numbers',
  original_order_tracking_carrier: 'Original Order Tracking Carriers',
} satisfies Record<
  (typeof AllReportColumns)[keyof typeof AllReportColumns],
  string
>;
